import { createSelector } from '@reduxjs/toolkit';
import { jwtDecode } from 'jwt-decode';

import { JwtToken, User } from '@paragon/contract-library';

import { LoginDataSliceState } from '../store/logInDataSlice';

export const getLoggedInUser = (state: LoginDataSliceState) => state.logInData.loggedInUser;
export const getJwtTokenData = createSelector(
  (state: LoginDataSliceState) => state.logInData.loggedInUser?.token,
  (token) => (token ? jwtDecode<JwtToken>(token) : null),
);

export function hasPermission(user: User, role: string) {
  return user.permissions.includes(role);
}

export const createPermissionSelector = (role: string) =>
  createSelector(getLoggedInUser, (loggedInUser) => loggedInUser != null && hasPermission(loggedInUser, role));

export const isLoggedInUserRoot = createPermissionSelector('root');
export const isLoggedInUserEngineer = createPermissionSelector('engineer');

export const hasAssemblyPermission = createPermissionSelector('assembly');
export const hasApiSessionsPermission = createPermissionSelector('api-sessions');
export const hasTrussSettingsPermission = createPermissionSelector('truss-settings');
export const hasTrussAnalysisPermission = createPermissionSelector('truss-analysis');
export const hasTrussAnalysisInteractPermission = createPermissionSelector('truss-analysis-interact');
export const hasTrussDesignPermission = createPermissionSelector('truss-design');
export const hasTrussDesignDownloadsPermission = createPermissionSelector('truss-design-downloads');
export const hasTrussPropertiesInteractPermission = createPermissionSelector('truss-properties-interact');
export const hasTrussConversionPermission = createPermissionSelector('truss-conversion');
export const hasMultiSelectPermission = createPermissionSelector('multi-select');
export const hasPricingPermission = createPermissionSelector('pricing-access');
export const hasNewLayoutModeFeatureFlag = createPermissionSelector('new-layout-mode');
export const hasLayoutOptimizationFeatureFlag = createPermissionSelector('layout-optimization');
export const hasSupervisorFeatureFlag = createPermissionSelector('production-supervisor');
export const hasProductionGroupManagementPermission = createPermissionSelector('production-group-management');
export const hasProjectHierarchyFeatureFlag = createPermissionSelector('project-hierarchy');
export const hasEditBearingConditionsPermission = createPermissionSelector('edit-bearing-conditions');
export const hasSealsAndSignaturesPermission = createPermissionSelector('seals-signatures-markup');
export const hasAnalysisEvaluationPermission = createPermissionSelector('analysis-evaluation');
export const hasMaterialsEditPermission = createPermissionSelector('can-edit-materials');
export const hasPricesEditPermission = createPermissionSelector('can-edit-prices');
export const hasOptimizationFeatureFlag = createPermissionSelector('optimization');
export const hasAutoCutOnOverlapFeatureFlag = createPermissionSelector('auto-cut-on-overlap');
export const hasParagonBrandedShareEmailsPermission = createPermissionSelector('paragon-branded-share-emails');
export const hasDesignValuesEditPermission = createPermissionSelector('can-edit-design-values');
export const hasSubscriptionManagerPermission = createPermissionSelector('subscription-manager');
export const hasHangerUpgradeAndAnalyzeFeatureFlag = createPermissionSelector('hanger-upgrade-and-analyze');
export const hasEndConditionEditingFeatureFlag = createPermissionSelector('edit-end-conditions');
export const hasLoadCaseCreationFeatureFlag = createPermissionSelector('create-load-cases');
export const hasCanUsePlatesPermission = createPermissionSelector('can-use-plates');
export const hasEngineerNetworkAccessPermission = createPermissionSelector('engineer-network-access');
export const hasOrganizationTemplatesPermission = createPermissionSelector('organization-templates');
export const hasScheduleAccess = createPermissionSelector('schedule-access');
export const hasProductionAccess = createPermissionSelector('production-access');
export const hasAutoLayoutFeatureFlag = createPermissionSelector('auto-layout');
export const hasComparePlatePricesPermission = createPermissionSelector('compare-plate-prices');
export const hasWebhooksPermission = createPermissionSelector('webhooks');
export const hasCrowdbuildStitcherImportFeatureFlag = createPermissionSelector('crowdbuild-stitcher-import');
export const hasAssistantFeatureFlag = createPermissionSelector('assistant');

// For usage tracking

// TODO: many usages of this selector violate the "permissions only grant capabilities" rule. Those should be removed
//   and replaced with more appropriate permission checks.
export const hasFreeSubscription = createPermissionSelector('free');
export const hasStandardSubscription = createPermissionSelector('standard');
export const hasTeamSubscription = createPermissionSelector('team');

export const isWithinFreeTrialPeriod = (user: User | null) =>
  user?.trialEndDate != null && new Date(user.trialEndDate) > new Date();

export const canUpgradeAccount = createSelector(
  hasSubscriptionManagerPermission,
  hasFreeSubscription,
  (subscriptionManager, free) => subscriptionManager && free,
);

export const canStartTrial = createSelector(
  canUpgradeAccount,
  getLoggedInUser,
  (canUpgrade, user) => canUpgrade && user != null && !user.trialActivated,
);

// we sometimes distinguish between self-service users and legacy users that Matt manually added, who we generally trust
//   more to access experimental features
export const isSelfServiceUser = createSelector(
  hasFreeSubscription,
  hasStandardSubscription,
  hasTeamSubscription,
  (free, standard, team) => free || standard || team,
);

export const shouldSynchronizeComponent = createPermissionSelector('sync-component');
export const userCanAccessProjectsClient = createPermissionSelector('projects-access');

export const hasBladeDownloadsPermission = createPermissionSelector('blade-downloads');
export const hasCybDownloadsPermission = createPermissionSelector('cyb-downloads');
export const hasTrsDownloadsPermission = createPermissionSelector('trs-downloads');
export const hasSteelTrsProfilePermission = createPermissionSelector('steel-trs-profile');

export const hasTrePreservePlateTypePermission = createPermissionSelector('tre-preserve-plate-type');

export const hasPdfSegmentUploadAiScalingFeatureFlag = createPermissionSelector('pdf-segment-upload-ai-scale');

export const getLoggedInUserOrganization = createSelector(getLoggedInUser, (loggedInUser) =>
  loggedInUser ? loggedInUser.organization : null,
);

export const canAccessInsertionFileUploader = createSelector(
  isLoggedInUserRoot,
  getLoggedInUserOrganization,
  (isRoot, organization) => isRoot || organization === 'Clearspan Components Inc.',
);

const organizationsExemptFromSessionLimitations = [
  'Paragon Component Systems',
  'Develop Organization A',
  'Develop Organization B',
  'Production Demo',
  'Layout Demo',
  'Design Demo',
];
export const isExemptFromSessionLimitation = createSelector(
  isLoggedInUserRoot,
  getLoggedInUserOrganization,
  (isRoot, organization) =>
    isRoot || (organization && organizationsExemptFromSessionLimitations.includes(organization)),
);
